import { reactive, computed, ref, readonly, watch, h, type ComputedRef } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { useUserStore } from '@/store/user'
import { usePayStore } from '@/store/pay'
import { logger } from '@/utils/logger'
import { useEventTracking } from '@/hooks/useEventTracking'
import { userRightStore } from '@/hooks/useUserRights'
import { useTeamEditionStore } from '@/store/teamEdition'
import { useTempRecordStore, TempRecordType, TEMP_RECORD_KEYS } from '@/store/tempRecord'
import { openModal } from '@/utils/modal'
import SSVIPConcurrentLimitModal from '@/components/Modals/SSVIPConcurrentLimitModal/index.vue'
import SvgIcon from '@/components/SvgIcon/index.vue'
import { TEAM_QUOTA_EXCEEDED_CODE, TEAM_QUOTA_EXCEEDED_TITLE, TEAM_QUOTA_EXCEEDED_CONTENT, shouldShowTeamQuotaExceededModal } from '@/utils/teamQuotaWarning'
import { MODEL_PERMISSION_DENIED_CONTENT, MODEL_PERMISSION_DENIED_CODE } from '@/utils/modelPermissionDenied'
import { ModuleId } from '@xmiles/holopix-types'
import {
  createGenerationTask,
  GenerationTaskSimulator,
  ProgressStatus,
  SimulationStage,
  type SimulationState,
  type UserType,
} from '@xmiles/holopix-ai'
import type { ResolvedTask } from './useRecoverPendingTasks'
import type { WaitTask, VersionRecord } from '../components/ChatPanel/types'

export type ArtworkTaskType = 'live' | 'recovered'

export interface ArtworkGenerationTask {
  clientId: string
  artworkId: number
  type: ArtworkTaskType
  status: number
  module: number | null
  param?: string | Record<string, unknown>
  createdAt: number
  updatedAt: number
  waitTask?: WaitTask
  version?: VersionRecord
  simulationState?: SimulationState
  error?: GenerationErrorInfo
  isEnhancingPrompt?: boolean
}

export interface ArtworkRunTaskOptions {
  module: number
  params: Record<string, unknown>
  userTier?: UserType
  /**
   * 自定义预计总时间（秒）
   * 覆盖 helper 包 getConsumeTime 的计算结果
   * 用于 helper 包未支持的模块（如重绘放大 module=58/59）
   */
  estimatedTimeOverride?: number
  onClientId?: (clientId: string) => void
  onProgress?: (state: SimulationState) => void
  onComplete?: (images: Array<{ image: string }>, resultData?: Record<string, unknown>) => void
  onError?: (error: GenerationErrorInfo) => void
}

export interface GenerationTaskServiceContext {
  userId: string | number
  boardId: number
  artworkId: number
  uniqueId: string
}

export interface GenerationErrorInfo {
  status: number
  message: string
  clientId: string
  module?: number | null
}

export const ERROR_STATUS = {
  GENERAL: -1,
  SENSITIVE_WORD: -2,
  LOGIN_EXPIRED: -3,
  INSUFFICIENT_CREDITS: -100,
  CONCURRENT_TASK: -200,
  PACKAGE_LIMIT: -201,
  VIP_REQUIRED: -202,
  VIP_FORBIDDEN: -203,
  MODEL_NOT_SUPPORTED: -300,
  SERVER_RECONNECT: -999,
  /** 团队个人额度已用尽（后端返回正数状态码） */
  TEAM_QUOTA_EXCEEDED: TEAM_QUOTA_EXCEEDED_CODE,
  /** 模型使用权限不足 */
  MODEL_PERMISSION_DENIED: MODEL_PERMISSION_DENIED_CODE,
} as const

function createArtworkGenerationTaskManager(initialContextGetter: () => GenerationTaskServiceContext) {
  let contextGetter = initialContextGetter
  const taskStateMap = reactive<Record<string, ArtworkGenerationTask>>({})
  const taskCallbacks = new Map<string, Set<(task: ArtworkGenerationTask | undefined) => void>>()
  const taskComputedCache = new Map<string, ComputedRef<ArtworkGenerationTask | undefined>>()
  const recoveredInitialArtworkId = ref(0)
  const recoveredInitialRevision = ref(0)

  watch(
    () => contextGetter()?.artworkId ?? 0,
    (nextArtworkId, prevArtworkId) => {
      if (nextArtworkId === prevArtworkId) return
      hardReset(nextArtworkId)
    },
    { flush: 'sync' },
  )

  const syncInitialTasks = (tasks: WaitTask[]) => {
    const inferredArtworkId = tasks?.[0]?.artworkId ?? (contextGetter()?.artworkId ?? 0)
    recoveredInitialArtworkId.value = inferredArtworkId
    recoveredInitialRevision.value += 1
    tasks.forEach((task) => {
      upsertTask(task.clientId, {
        artworkId: task.artworkId,
        type: 'recovered',
        status: normalizeRecoveredStatus(task.status),
        module: typeof task.module === 'number' ? task.module : null,
        param: task.param,
        waitTask: task,
      })
    })
  }

  const syncTaskProgress = (task: WaitTask) => {
    upsertTask(task.clientId, {
      artworkId: task.artworkId,
      type: 'recovered',
      status: normalizeRecoveredStatus(task.status),
      module: typeof task.module === 'number' ? task.module : null,
      param: task.param,
      waitTask: task,
    })
  }

  const syncTaskComplete = (resolvedTasks: ResolvedTask[]) => {
    resolvedTasks.forEach((resolvedTask) => {
      const status = resolveRecoveredFinalStatus(resolvedTask)

      if (status === -1) {
        const error: GenerationErrorInfo = {
          status: -1,
          message: '任务执行失败',
          clientId: resolvedTask.task.clientId,
          module: typeof resolvedTask.task.module === 'number' ? resolvedTask.task.module : null,
        }
        handleTaskError(error)
        upsertTask(resolvedTask.task.clientId, {
          artworkId: resolvedTask.task.artworkId,
          type: 'recovered',
          status: -1,
          module: typeof resolvedTask.task.module === 'number' ? resolvedTask.task.module : null,
          param: resolvedTask.task.param,
          waitTask: resolvedTask.task,
          version: resolvedTask.version,
          error,
        })
        return
      }

      upsertTask(resolvedTask.task.clientId, {
        artworkId: resolvedTask.task.artworkId,
        type: 'recovered',
        status,
        module: typeof resolvedTask.task.module === 'number' ? resolvedTask.task.module : null,
        param: resolvedTask.task.param,
        waitTask: resolvedTask.task,
        version: resolvedTask.version,
      })
    })
  }

  const allTasks = computed(() => {
    const currentArtworkId = contextGetter()?.artworkId ?? 0
    if (!currentArtworkId) return []
    return Object.values(taskStateMap)
      .filter((t) => t.artworkId === currentArtworkId)
      .sort((a, b) => b.updatedAt - a.updatedAt)
  })

  const activeTaskCount = computed(() => {
    return allTasks.value.filter((t) => t.status === 1 || t.status === 2).length
  })

  /**
   * 默认并发上限（原白名单档，已对全量用户/模型开放）：VIP 4 / SVIP 8 / 旗舰 20
   */
  const concurrentLimit = computed(() => {
    const userStore = useUserStore()
    if (userStore.highestPackage === 'ultra') return 20
    if (userStore.highestPackage === 'svip') return 8
    if (userStore.highestPackage === 'vip') return 4
    return 1
  })

  /**
   * 极速编辑 Lite 仍走旧档：VIP 2 / SVIP 4 / 旗舰 6
   */
  const getLiteConcurrentLimit = () => {
    const userStore = useUserStore()
    if (userStore.highestPackage === 'ultra') return 6
    if (userStore.highestPackage === 'svip') return 4
    if (userStore.highestPackage === 'vip') return 2
    return 1
  }

  const getEffectiveConcurrentLimit = (module?: number) => {
    if (module === ModuleId.KleinFast) {
      return getLiteConcurrentLimit()
    }
    return concurrentLimit.value
  }

  const handleTaskError = (
    error: GenerationErrorInfo,
    context?: { vipChannel?: boolean },
  ) => {
    const msg = handleGenerationError(error, context)
    if (msg) {
      // 为了避免与画板占位符或聊天面板上的错误状态重复显示（用户反馈有两个失败消息）
      // 这里过滤掉常规的失败状态，不显示全局 Toast，由具体的业务 UI 组件处理显示
      const silentStatuses: number[] = [ERROR_STATUS.GENERAL, ERROR_STATUS.SERVER_RECONNECT, ERROR_STATUS.SENSITIVE_WORD]
      if (!silentStatuses.includes(error.status)) {
        message.error(msg)
      }
      const { status } = error
      const toastType = status === ERROR_STATUS.INSUFFICIENT_CREDITS ? 'power_empty' : 'generation_fail'
      useEventTracking({
        event: 'canvas_toast_event',
        eventValue: {
          view: 'view_canvas_detail',
          toast_type: toastType,
          trigger_action: 'generation',
        },
      })
    }
  }


  const startTask = (options: ArtworkRunTaskOptions) => {
    let runtimeClientId = ''
    let isFinalized = false
    const userRightsHook = userRightStore()

    try {
      if (activeTaskCount.value >= getEffectiveConcurrentLimit(options.module)) {
        const userStore = useUserStore()
        const teamEditionStore = useTeamEditionStore()
        const isTeamContext = teamEditionStore.isTeamEdition || userStore.isGroupLogin
        const messageText = isTeamContext && !userStore.hasMembershipAtLeast('svip')
          ? '请联系客服升级专业版'
          : '已达到并发上限，请稍作等待'

        const presetClientIdRaw = (options.params as any)?.clientId
        const presetClientId = typeof presetClientIdRaw === 'string' ? presetClientIdRaw : ''
        const error: GenerationErrorInfo = {
          status: ERROR_STATUS.CONCURRENT_TASK,
          message: messageText,
          clientId: presetClientId || 'local',
          module: options.module,
        }
        handleTaskError(error, { vipChannel: !!options.params?.vipChannel })
        options.onError?.(error)
        return
      }

      const context = contextGetter()
  
      const task = createGenerationTask({
        module: options.module,
        userId: context.userId,
        artworkId: context.artworkId,
        uniqueId: context.uniqueId,
        boardId: context.boardId,
        ...options.params,
      })

      runtimeClientId = task.clientId || ''
      if (runtimeClientId) {
        upsertTask(runtimeClientId, {
          artworkId: context.artworkId,
          type: 'live',
          status: 1,
          module: options.module,
          param: options.params,
          isEnhancingPrompt: false,
        })
        options.onClientId?.(runtimeClientId)
      }

      new GenerationTaskSimulator(task, options.userTier, (state) => {
        if (!runtimeClientId) return

        /**
         * 几种状态需要刷新算力
         */
        if ([ProgressStatus.CONNECTED].includes(state.realStatus!)) {
          logger.info('更新算力', { clientId: runtimeClientId })
          userRightsHook.refreshUserRights()
        } else if (state.stage === SimulationStage.COMPLETED) {
          logger.info('更新算力', { clientId: runtimeClientId })
          userRightsHook.refreshUserRights()
        } else if (state.stage === SimulationStage.ERROR) {
          logger.info('更新算力', { clientId: runtimeClientId })
          userRightsHook.refreshUserRights()
        }


        const nextStatus = mapSimulationStageToStatus(state.stage)

        // 重绘放大等 helper 包未支持的模块：按比例缩放 remainingTime
        // Simulator 内部基于 helper 包 default(60s) 倒计时，需缩放到实际预计时间
        let simulationStateToStore: SimulationState = state
        if (
          options.estimatedTimeOverride != null &&
          state.remainingTime != null &&
          state.remainingTime > 0
        ) {
          const helperDefaultTime = 60
          const ratio = options.estimatedTimeOverride / helperDefaultTime
          simulationStateToStore = {
            ...state,
            remainingTime: Math.max(0, Math.ceil(state.remainingTime * ratio)),
          }
        }

        upsertTask(runtimeClientId, {
          artworkId: context.artworkId,
          type: 'live',
          status: nextStatus,
          module: options.module,
          param: options.params,
          simulationState: simulationStateToStore,
        })

        if (state.stage === SimulationStage.COMPLETED) {
          if (isFinalized) return
          isFinalized = true
          const images = (state?.data?.images ?? []) as Array<{ image: string }>
          options.onComplete?.(images, state?.data as Record<string, unknown> | undefined)
          return
        }

        if (state.stage === SimulationStage.ERROR) {
          if (isFinalized) return
          isFinalized = true
          const error: GenerationErrorInfo = {
            status: state?.data?.status ?? -1,
            // 优先取服务端返回的真实 errMsg，simulator 的 state.message 是通用文案 "生成失败，请重试"
            message: (state?.data as Record<string, unknown> | undefined)?.errMsg as string
              || state?.message
              || '生图失败',
            clientId: runtimeClientId,
            module: options.module,
          }

          upsertTask(runtimeClientId, {
            artworkId: context.artworkId,
            type: 'live',
            status: -1,
            module: options.module,
            param: options.params,
            error,
          })
          handleTaskError(error, { vipChannel: !!options.params?.vipChannel })
          options.onError?.(error)
          return
        }

        options.onProgress?.(state)
      })
    } catch (error) {
      const runtimeError: GenerationErrorInfo = {
        status: -1,
        message: error instanceof Error ? error.message : '生图任务创建失败',
        clientId: runtimeClientId,
        module: options.module,
      }
      if (runtimeClientId) {
        upsertTask(runtimeClientId, {
          artworkId: contextGetter()?.artworkId ?? 0,
          type: 'live',
          status: -1,
          module: options.module,
          param: options.params,
          error: runtimeError,
        })
      }
      handleTaskError(runtimeError, { vipChannel: !!options.params?.vipChannel })
      options.onError?.(runtimeError)
    }
  }

  const useTaskByClientId = (clientId: string): ComputedRef<ArtworkGenerationTask | undefined> => {
    const cached = taskComputedCache.get(clientId)
    if (cached) return cached
    const taskRef = computed(() => taskStateMap[clientId])
    taskComputedCache.set(clientId, taskRef)
    return taskRef
  }

  const onTaskChange = (
    clientId: string,
    callback: (task: ArtworkGenerationTask | undefined) => void,
  ) => {
    if (!taskCallbacks.has(clientId)) {
      taskCallbacks.set(clientId, new Set())
    }
    taskCallbacks.get(clientId)!.add(callback)
    return () => {
      const callbacks = taskCallbacks.get(clientId)
      if (!callbacks) return
      callbacks.delete(callback)
      if (callbacks.size === 0) {
        taskCallbacks.delete(clientId)
      }
    }
  }

  const removeTask = (clientId: string) => {
    if (!(clientId in taskStateMap)) return
    delete taskStateMap[clientId]
    taskComputedCache.delete(clientId)
    notifyTaskChange(clientId)
  }

  const reset = () => {
    Object.keys(taskStateMap).forEach((clientId) => {
      delete taskStateMap[clientId]
    })
    taskComputedCache.clear()
  }

  return {
    allTasks,
    activeTaskCount,
    concurrentLimit,
    getEffectiveConcurrentLimit,
    startTask,
    useTaskByClientId,
    onTaskChange,
    removeTask,
    reset,
    setContextGetter,
    syncInitialTasks,
    syncTaskProgress,
    syncTaskComplete,
    upsertTask,
    recoveredInitialArtworkId: readonly(recoveredInitialArtworkId),
    recoveredInitialRevision: readonly(recoveredInitialRevision),
  }

  function hardReset(nextArtworkId: number) {
    Object.keys(taskStateMap).forEach((clientId) => {
      delete taskStateMap[clientId]
    })
    taskCallbacks.clear()
    taskComputedCache.clear()
    recoveredInitialArtworkId.value = nextArtworkId
    recoveredInitialRevision.value = 0
  }

  function setContextGetter(nextGetter: () => GenerationTaskServiceContext) {
    contextGetter = nextGetter
  }

  function upsertTask(clientId: string, patch: Omit<Partial<ArtworkGenerationTask>, 'clientId' | 'updatedAt'>) {
    const now = Date.now()
    const currentArtworkId = contextGetter()?.artworkId ?? 0
    const inferredArtworkId = typeof patch.artworkId === 'number' ? patch.artworkId : currentArtworkId

    // 如果任务的 Artwork ID 与当前上下文的 Artwork ID 不一致，则忽略该任务更新
    // 这防止了旧任务（可能在后台运行）污染当前的任务列表
    if (inferredArtworkId !== 0 && currentArtworkId !== 0 && inferredArtworkId !== currentArtworkId) {
      return
    }

    const current = taskStateMap[clientId]
    taskStateMap[clientId] = current
      ? {
        ...current,
        ...patch,
        clientId,
        artworkId: inferredArtworkId,
        updatedAt: now,
      }
      : {
        clientId,
        artworkId: inferredArtworkId,
        type: patch.type ?? 'live',
        status: patch.status ?? 1,
        module: patch.module ?? null,
        createdAt: now,
        updatedAt: now,
        param: patch.param,
        waitTask: patch.waitTask,
        version: patch.version,
        simulationState: patch.simulationState,
        error: patch.error,
      }
    notifyTaskChange(clientId)
  }

  function notifyTaskChange(clientId: string) {
    const callbacks = taskCallbacks.get(clientId)
    if (!callbacks) return
    const task = taskStateMap[clientId]
    callbacks.forEach((callback) => callback(task))
  }
}

type ArtworkGenerationTaskManager = ReturnType<typeof createArtworkGenerationTaskManager>

let artworkGenerationTaskManager: ArtworkGenerationTaskManager | null = null

export function useArtworkGenerationTaskManager(getContext: () => GenerationTaskServiceContext) {
  if (!artworkGenerationTaskManager) {
    artworkGenerationTaskManager = createArtworkGenerationTaskManager(getContext)
    return artworkGenerationTaskManager
  }

  artworkGenerationTaskManager.setContextGetter(getContext)
  return artworkGenerationTaskManager
}

function mapSimulationStageToStatus(stage: SimulationStage): number {
  if (stage === SimulationStage.COMPLETED) return 3
  if (stage === SimulationStage.ERROR) return -1
  if (stage === SimulationStage.GENERATING) return 2
  return 1
}

function normalizeRecoveredStatus(status: number): number {
  if (status === 1 || status === 2 || status === 3 || status === -1) return status
  return 1
}

function resolveRecoveredFinalStatus(resolvedTask: ResolvedTask): number {
  if (!resolvedTask.isSuccess) return -1
  if (typeof resolvedTask.version?.status === 'number') return resolvedTask.version.status
  return 3
}

export function handleGenerationError(
  error: GenerationErrorInfo,
  context?: { vipChannel?: boolean },
): string {
  const userStore = useUserStore()
  const teamEditionStore = useTeamEditionStore()
  const payStore = usePayStore()
  const { status, message: serverMsg, clientId } = error
  let displayMsg = serverMsg || '生图失败'
  const isTeamContext = teamEditionStore.isTeamEdition || userStore.isGroupLogin

  switch (status) {
    case ERROR_STATUS.GENERAL:
      logger.error('生图一般错误', { clientId, errorMsg: serverMsg })
      displayMsg = serverMsg || '生图失败，请重试'
      break

    case ERROR_STATUS.SENSITIVE_WORD:
      logger.error('生图敏感词审查失败', { clientId })
      displayMsg = '内容安全审查未通过，请修改提示词或图片'
      break

    case ERROR_STATUS.LOGIN_EXPIRED:
      logger.error('生图登录失效', { clientId })
      displayMsg = '生图失败，登录已失效'
      break

    case ERROR_STATUS.INSUFFICIENT_CREDITS: {
      logger.error('生图算力不足', { clientId, highestPackage: userStore.highestPackage })
      payStore.ballParams.button = 'button_creation_img_not_points'
      userStore.usePayModal({ freeToast: false, powerPackage: true })
      if (userStore.hasMembershipAtLeast('svip') && context?.vipChannel) {
        displayMsg = '生图失败，您的会员加速算力已不足'
      } else {
        displayMsg = '生图失败，您的算力已不足'
      }
      break
    }

    case ERROR_STATUS.CONCURRENT_TASK: {
      logger.error('生图并发任务错误', { clientId })
      payStore.ballParams.button = 'button_creation_img_concurrent'

      const highestPackage = userStore.highestPackage

      if (isTeamContext && !userStore.hasMembershipAtLeast('svip')) {
        displayMsg = '已达到生成队列上限，请联系客服升级专业版'
        error.message = displayMsg
        break
      }

      if (highestPackage === 'free') {
        userStore.usePayModal({ freeToast: false })
        displayMsg = '已达到生成队列上限，请升级会员'
      } else if (highestPackage === 'vip') {
        payStore.useProductModal('member')
        displayMsg = '已达到生成队列上限，请升级SVIP增加更多队列'
      } else if (highestPackage === 'ultra') {
        const tempRecordStore = useTempRecordStore()
        const modalKey = TEMP_RECORD_KEYS.SVIP_CONCURRENT_LIMIT_MODAL_SHOWN
        if (!tempRecordStore.hasRecord(modalKey)) {
          tempRecordStore.setRecord(modalKey, true, TempRecordType.DAILY_RESET)
          openModal('modal-ssvip-concurrent-limit', {
            component: SSVIPConcurrentLimitModal,
          })
          displayMsg = ''
        } else {
          // 当日已弹出过弹窗，不再重复弹窗，降级展示文案提示
          displayMsg = '已达到生成队列上限，请稍作等待'
        }

      } else if (highestPackage === 'svip') {
        payStore.useProductModal('member', false, { defaultMemberType: 'ultra' })
        displayMsg = '已达到生成队列上限，请升级旗舰版增加更多队列'
      } else {
        userStore.usePayModal()
        displayMsg = '生图失败，当前已有任务进行中，请稍后提交'
      }
      break
    }

    case ERROR_STATUS.PACKAGE_LIMIT:
      logger.error('生图套餐权益错误', { clientId, showVip: userStore.showVip })
      if (isTeamContext) {
        displayMsg = '请联系客服升级专业版'
        error.message = displayMsg
      } else {
        userStore.usePayModal()
        displayMsg = userStore.showVip ? '生图失败，生图数量不符合当前套餐权益' : '生图失败'
      }
      break

    case ERROR_STATUS.VIP_REQUIRED:
      logger.error('生图VIP权益错误', { clientId, showVip: userStore.showVip })
      if (isTeamContext) {
        displayMsg = '请联系客服升级专业版'
        error.message = displayMsg
      } else {
        userStore.usePayModal()
        displayMsg = userStore.showVip ? '生图失败，倍数下载需vip权益' : '生图失败'
      }
      break

    case ERROR_STATUS.VIP_FORBIDDEN:
      logger.error('生图会员权益使用错误', { clientId, showVip: userStore.showVip })
      if (isTeamContext) {
        displayMsg = '请联系客服升级专业版'
        error.message = displayMsg
      } else {
        userStore.usePayModal()
        displayMsg = userStore.showVip ? '生图失败，您不能使用会员权益' : '生图失败'
      }
      break

    case ERROR_STATUS.MODEL_NOT_SUPPORTED:
      logger.error('生图模型不支持错误', { clientId })
      // displayMsg = '生图失败，当前模型不支持，请尝试其他模型'
      displayMsg = ''
      break

    case ERROR_STATUS.SERVER_RECONNECT:
      logger.warn('服务器要求重连', { clientId })
      displayMsg = '生图失败，请重试'
      break

    case ERROR_STATUS.TEAM_QUOTA_EXCEEDED:
      logger.error('团队个人额度已用尽', { clientId })
      if (shouldShowTeamQuotaExceededModal()) {
        Modal.warning({
          title: TEAM_QUOTA_EXCEEDED_TITLE,
          icon: h(SvgIcon, { directory: 'others', name: 'tip-modal-icon', size: 24, class: 'anticon' }),
          content: TEAM_QUOTA_EXCEEDED_CONTENT,
          okText: '我知道了',
          okType: 'primary',
          wrapClassName: 'custom-tip-modal',
          bodyStyle: { padding: '16px' },
          centered: true,
        })
      }
      displayMsg = ''
      break

    case ERROR_STATUS.MODEL_PERMISSION_DENIED:
      logger.error('模型使用权限不足', { clientId })
      displayMsg = MODEL_PERMISSION_DENIED_CONTENT
      break

    default:
      logger.warn('生图未知错误', { clientId, status })
      displayMsg = serverMsg || '生图失败'
      break
  }

  return displayMsg
}
